import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function MapDialog({ children, latitude, longitude, trialName }) {
  // Parse coordinates that might be in format "E:10,11127" or "N:55,29742"
  const parseCoordinate = (coord) => {
    if (!coord) return NaN;

    // Remove E: or N: prefix and replace comma with dot
    const cleanCoord = coord
      .toString()
      .replace(/^[EN]:/i, "") // Remove E: or N: prefix (case insensitive)
      .replace(",", "."); // Replace comma with dot for decimal

    return parseFloat(cleanCoord);
  };

  const lng = parseCoordinate(longitude); // E: = longitude
  const lat = parseCoordinate(latitude); // N: = latitude

  // Validate coordinates
  if (isNaN(lat) || isNaN(lng)) {
    return <span>{children}</span>; // Return non-clickable children if invalid coordinates
  }

  // Generate different map service URLs
  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}&z=15`;
  const openStreetMapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`;
  const appleMapsUrl = `https://maps.apple.com/?ll=${lat},${lng}&z=15`;

  // Embed map URL (using OpenStreetMap for free usage)
  const embedMapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${
    lng - 0.01
  },${lat - 0.01},${lng + 0.01},${
    lat + 0.01
  }&layer=mapnik&marker=${lat},${lng}`;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <span className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline transition-all duration-200">
          {children}
        </span>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            {trialName ? `${trialName} Location` : "Trial Location"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Embedded Map */}
          <div className="w-full h-96 border rounded-lg overflow-hidden">
            <iframe
              src={embedMapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              title="Trial Location Map"
              loading="lazy"
            />
          </div>

          {/* External Map Links */}
          <div className="flex gap-4 justify-center">
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Google Maps
              </Button>
            </a>
            <a
              href={openStreetMapUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-green-600 hover:bg-green-700">
                OpenStreetMap
              </Button>
            </a>
            <a href={appleMapsUrl} target="_blank" rel="noopener noreferrer">
              <Button className="bg-red-600 hover:bg-red-700">
                Apple Maps
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
